import {
  FEATURE_LIMIT_CONTACT_EMAIL,
  FEATURE_LIMIT_NOUN_PLURAL,
  featureLimitReachedHint,
  formatFeatureLimitUsage,
  isAtFeatureLimit,
  isNearFeatureLimit,
  type FeatureLimitKey,
} from '../lib/instanceFeatureLimits';

type Props = {
  limitKey: FeatureLimitKey;
  used: number;
  max: number;
  className?: string;
  /** Hide until /api/me/feature-limits has resolved (avoids a wrong default max). */
  ready?: boolean;
  /** `chip` fits into a toolbar row next to search/filter controls. */
  variant?: 'block' | 'chip';
};

export function FeatureLimitHint({
  limitKey,
  used,
  max,
  className,
  ready = true,
  variant = 'block',
}: Props) {
  if (!ready) return null;
  const at = isAtFeatureLimit(used, max);
  const near = isNearFeatureLimit(used, max);

  if (variant === 'chip') {
    const tone = at
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : near
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-app-border bg-app-surface text-app-text-muted';
    return (
      <span
        className={`inline-flex h-8 shrink-0 items-center rounded-md border px-2.5 text-xs font-medium ${tone} ${className ?? ''}`}
        title={at ? featureLimitReachedHint(limitKey, used, max) : undefined}
      >
        {formatFeatureLimitUsage(limitKey, used, max)}
        {at ? (
          <span className="sr-only">
            {' '}
            Limit erreicht. Für weitere {FEATURE_LIMIT_NOUN_PLURAL[limitKey]} kontaktiere uns unter{' '}
            {FEATURE_LIMIT_CONTACT_EMAIL}.
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <div className={`inline-flex min-w-0 shrink-0 flex-col ${className ?? ''}`}>
      <p className={at || near ? 'text-sm text-app-text-muted' : 'text-sm text-app-text-subtle'}>
        {formatFeatureLimitUsage(limitKey, used, max)}
      </p>
      {at ? (
        <p className="mt-1 text-sm text-app-text-subtle">
          Limit erreicht. Für weitere {FEATURE_LIMIT_NOUN_PLURAL[limitKey]} kontaktiere uns unter{' '}
          <a href={`mailto:${FEATURE_LIMIT_CONTACT_EMAIL}`} className="underline">
            {FEATURE_LIMIT_CONTACT_EMAIL}
          </a>
          .
        </p>
      ) : null}
    </div>
  );
}
